// @flow
import env from '../../../environment';
import {postCreator, getCreator, putCreator} from '../../../http';

import type {StoreFeature} from '../../../entities/store-feature';

export const endpoints = Object.freeze({
  CREATE_COLLABORATOR: `${env.baseApiUrl}/usher/auth/vendor/store/collaborator`,
  GET_COLLABORATORS: `${env.baseApiUrl}/usher/user/vendor/store/collaborator`,
  GET_COLLABORATOR: `${env.baseApiUrl}/usher/user/vendor/store/collaborator/:collaboratorId`,
  UPDATE_COLLABORATOR: `${env.baseApiUrl}/usher/user/vendor/store/collaborator/:collaboratorId/data`,
  UPDATE_COLLABORATOR_COLLABORATOR: `${env.baseApiUrl}/usher/user/collaborator/data`,

  COLLABORATOR_FORGOT_PASSWORD: `${env.baseApiUrl}/usher/auth/collaborator/forgot-password`,
  UPDATE_COLLABORATOR_PASSWORD: `${env.baseApiUrl}/usher/user/collaborator/password`,

  COLLABORATOR_EMAIL_EXISTS: `${env.baseApiUrl}/usher/auth/collaborator/email/exists`,
});

export type SaveCollaboratorRequest = {
  email?: string,
  password?: string,
  firstName?: string,
  lastName?: string,
  phone?: string,
  storeFeatures?: string[],
  archived?: boolean,
};
export type SaveSellerRequest = {};

export const createCollaboratorApi = postCreator(endpoints.CREATE_COLLABORATOR);
export const getCollaboratorsApi = getCreator(endpoints.GET_COLLABORATORS);
export const getCollaboratorApi = getCreator(endpoints.GET_COLLABORATOR);
export const updateCollaboratorApi = putCreator(endpoints.UPDATE_COLLABORATOR);
export const updateCollaboratorCollaboratorApi = putCreator(
  endpoints.UPDATE_COLLABORATOR_COLLABORATOR
);

export const collaboratorForgotPasswordApi = postCreator(
  endpoints.COLLABORATOR_FORGOT_PASSWORD
);
export const updateCollaboratorPasswordApi = putCreator(
  endpoints.UPDATE_COLLABORATOR_PASSWORD
);

export const collaboratorEmailExists = postCreator(
  endpoints.COLLABORATOR_EMAIL_EXISTS
);

export type CollaboratorFeatureItem = {
  id: string,
  name?: string,
  features: StoreFeature[],
  dependencies?: StoreFeature[],
};

export type CollaboratorFeatureGroup = {
  id: string,
  name: string,
  description?: string,
  items: CollaboratorFeatureItem[],
};

export const COLLABORATOR_FEATURE_GROUPS: CollaboratorFeatureGroup[] = [
  {
    id: 'orders',
    name: 'Pedidos',
    description: 'Gestión de pedidos',
    items: [
      {id: 'ORDER_STATUS_CHANGE', features: ['ORDER_STATUS_CHANGE']},
      {
        id: 'ORDER_EDIT',
        name: 'Editar pedidos',
        features: [
          'ORDER_EDIT_CUSTOMER_DATA',
          'ORDER_EDIT_ITEMS',
          'ORDER_EDIT_PAYMENT_DATA',
          'ORDER_EDIT_DELIVERY_DATA',
          'ORDER_EDIT_ADDITIONAL_INFORMATION',
        ],
      },
      {
        id: 'ORDER_TOOLS',
        name: 'Herramientas de pedidos',
        features: [
          'ORDER_EDIT_COLLABORATOR',
          'ORDER_EXPORT',
          'ORDER_NOTIFICATIONS',
          'ORDER_COMMENTS',
          'ORDER_FILTER',
          'ORDER_REVIEW',
        ],
      },
      {
        id: 'ORDER_CREATION',
        name: 'Crear pedidos manuales',
        features: [
          'MANUAL_ORDER_CREATION',
          'PRE_ORDER',
          'CUSTOM_ORDER_CHECKOUT',
          'ORDER_REQUIRED_INFORMATION',
          'ORDER_STATE_CHANGE_MESSAGES',
        ],
      },
    ],
  },
  {
    id: 'products',
    name: 'Productos',
    description: 'Gestión del catálogo',
    items: [
      {id: 'PRODUCT_CREATION', features: ['PRODUCT_CREATION']},
      {
        id: 'PRODUCT_EDIT_FULL',
        name: 'Editar productos',
        features: [
          'PRODUCT_EDIT',
          'STOCK_BY_PRODUCT',
          'PRODUCT_VARIANTS',
          'BULK_PRICE',
          'FEATURED_PRODUCTS',
          'PRODUCT_OPTIONS',
          'SORT_PRODUCT_IMAGES',
          'PRODUCT_VIDEO_URL',
          'PRODUCT_SPECIFICATIONS',
          'PRODUCT_SIZE_CHART',
          'RELATED_PRODUCTS',
          'PRODUCT_SEO',
          'PRODUCT_DISABLE',
          'PRODUCT_SECTIONS',
        ],
      },
      {id: 'PRODUCT_DELETE', features: ['PRODUCT_DELETE']},
      {
        id: 'PRODUCT_BULK',
        name: 'Carga masiva',
        features: [
          'MASSIVE_PRODUCT_UPLOAD',
          'PRODUCTS_BULK_EDIT',
          'INSTAGRAM_PRODUCT_UPLOAD',
          'PRODUCT_CATALOGUE',
        ],
      },
      {
        id: 'CATEGORIES',
        name: 'Categorías',
        features: [
          'PRODUCT_CATEGORIES',
          'CATEGORY_PHOTOS',
          'PRODUCT_CATEGORY_LAYOUT',
        ],
      },
    ],
  },
  {
    id: 'store',
    name: 'Tienda',
    description: 'Configuración de la tienda',
    items: [
      {
        id: 'STORE_INFO',
        name: 'Información y políticas',
        features: [
          'STORE_INFORMATION',
          'STORE_PROFILE_INFORMATION',
          'STORE_SOCIAL_MEDIA',
          'STORE_POLICIES',
          'STORE_LOCATION',
        ],
      },
      {
        id: 'STORE_DESIGN',
        name: 'Diseño y apariencia',
        features: [
          'COLOR_CONFIGURATION',
          'ADVANCED_COLOR_CONFIGURATION',
          'FONT_CONFIGURATION',
          'IMAGE_CONFIGURATION',
          'STOREFRONT_TEMPLATES',
          'STORE_FAVICON',
          'CUSTOM_DOMAIN',
        ],
      },
      {
        id: 'STORE_CONTENT',
        name: 'Contenido (banners, bloques)',
        features: [
          'COVER_PHOTO',
          'STORE_HEADER',
          'STORE_PAGE_LAYOUT',
          'STORE_INFORMATION_BLOCK',
          'STORE_POP_UP',
          'STORE_BLOG',
          'BANNER',
          'BANNER_GROUP',
        ],
      },
    ],
  },
  {
    id: 'sales',
    name: 'Ventas',
    description: 'Dashboard, pagos, envíos y promociones',
    items: [
      {
        id: 'DASHBOARD',
        name: 'Dashboard',
        features: [
          'DASHBOARD_BASIC',
          'DASHBOARD_PRO',
          'DASHBOARD_COLLABORATOR',
        ],
      },
      {
        id: 'PAYMENTS',
        name: 'Pagos',
        features: [
          'PAYMENT_PROCESSOR',
          'MERCADOPAGO_CHECKOUT_PRO_INTEGRATION',
          'MY_PAYMENT_INFORMATION',
        ],
      },
      {id: 'DELIVERY_OPTIONS', features: ['DELIVERY_OPTIONS']},
      {
        id: 'PROMOTIONS',
        name: 'Promociones y cupones',
        features: ['STORE_PROMOTION', 'STORE_COUPON'],
      },
    ],
  },
  {
    id: 'customers',
    name: 'Clientes',
    description: 'CRM y facturación',
    items: [
      {
        id: 'CRM',
        name: 'Gestión de clientes',
        features: ['CRM_CUSTOMER', 'CRM_SEGMENT', 'CUSTOMER_COMPLAINT'],
      },
      {
        id: 'BILLING',
        name: 'Facturación',
        features: ['TAXES', 'TAX_INVOICE', 'STORE_TAX_INVOICE_SETTING'],
      },
    ],
  },
  {
    id: 'integrations',
    name: 'Integraciones',
    description: 'Analytics, píxeles y TiendaChat',
    items: [
      {
        id: 'ANALYTICS',
        name: 'Analytics y píxeles',
        features: [
          'GOOGLE_ANALYTICS_TRACKING',
          'GOOGLE_TAG_MANAGER',
          'FACEBOOK_PIXEL_ID',
          'FACEBOOK_VALIDATION_TOKEN',
          'TIKTOK_PIXEL_ID',
          'FB_STORE',
        ],
      },
      {
        id: 'TIENDACHAT',
        name: 'TiendaChat',
        features: [
          'TIENDACHAT_WHATSAPP_WEB',
          'TIENDACHAT_WHATSAPP_API',
          'TIENDACHAT_FB_MESSENGER',
          'TIENDACHAT_INSTAGRAM',
          'TIENDACHAT_QUICK_REPLIES',
          'TIENDACHAT_TRIGGERS',
          'TIENDACHAT_AI_CHATBOT',
          'FUNNEL',
        ],
      },
    ],
  },
];

export const COLLABORATORS_FEATURES_MAP = Object.freeze({
  TOOLS:
    'ORDER_EDIT_COLLABORATOR,ORDER_EXPORT,ORDER_NOTIFICATIONS,PRODUCT_CATALOGUE',
  MANUAL_ORDER: 'MANUAL_ORDER_CREATION,PRE_ORDER,CUSTOM_ORDER_CHECKOUT',
  ORDER_EDIT:
    'ORDER_EDIT_CUSTOMER_DATA,ORDER_EDIT_ITEMS,ORDER_EDIT_PAYMENT_DATA,ORDER_EDIT_DELIVERY_DATA,ORDER_EDIT_ADDITIONAL_INFORMATION',
  ORDER_STATUS_CHANGE: 'ORDER_STATUS_CHANGE',
  PRODUCT_CREATION:
    'PRODUCT_CREATION,BULK_PRICE,FEATURED_PRODUCTS,PRODUCT_VARIANTS,STOCK_BY_PRODUCT,PRODUCT_VIDEO_URL,PRODUCT_SPECIFICATIONS,PRODUCT_SIZE_CHART,RELATED_PRODUCTS,PRODUCT_SEO,PRODUCT_LIVE_VIEWS,PRODUCT_SALES,PRODUCT_CTA,HOME_CATEGORIES_CAROUSEL,PRODUCT_SECTIONS',
  PRODUCT_EDIT:
    'PRODUCT_EDIT,BULK_PRICE,PRODUCT_VIDEO_URL,STOCK_BY_PRODUCT,PRODUCT_VARIANTS,FEATURED_PRODUCTS,PRODUCT_CATEGORIES,MASSIVE_PRODUCT_UPLOAD,INSTAGRAM_PRODUCT_UPLOAD,PRODUCT_SPECIFICATIONS,PRODUCT_SIZE_CHART,RELATED_PRODUCTS,PRODUCT_SEO,PRODUCT_LIVE_VIEWS,PRODUCT_SALES,PRODUCT_CTA,HOME_CATEGORIES_CAROUSEL,PRODUCT_SECTIONS',
  PRODUCT_DELETE: 'PRODUCT_DELETE',
  PRODUCT_DISABLE: 'PRODUCT_DISABLE',
  STORE_INFORMATION:
    'STORE_INFORMATION,STORE_PROFILE_INFORMATION,STORE_SOCIAL_MEDIA,STORE_POLICIES,COVER_PHOTO,COLOR_CONFIGURATION,ADVANCED_COLOR_CONFIGURATION,STOREFRONT_TEMPLATES,FONT_CONFIGURATION,IMAGE_CONFIGURATION,STORE_PROFILE_INFORMATION',
  SALES_PROCESS: 'ORDER_REQUIRED_INFORMATION,ORDER_STATE_CHANGE_MESSAGES',
  DASHBOARD_COLLABORATOR: 'DASHBOARD_COLLABORATOR',
  SHIPPING_CONFIGURE:
    'SHIPPING_METHOD,PICKUP,ADVANCED_SHIPPING_METHOD,MY_PAYMENT_INFORMATION',
  PAYMENT_CONFIGURE: 'PAYMENT_PROCESSOR,MERCADOPAGO_CHECKOUT_PRO_INTEGRATION',
  PAGE_SETTINGS:
    'BANNER,STORE_HEADER,STORE_PAGE_LAYOUT,PRODUCT_CATEGORY_LAYOUT,PRODUCT_CATEGORY_LAYOUT',
  STORE_PROMOTIONS:
    'STORE_PROMOTION,STORE_COUPON,ORDER_REVIEW,MARKETING_TEMPLATES',
  INTEGRATIONS:
    'GOOGLE_ANALYTICS_TRACKING,GOOGLE_TAG_MANAGER,FB_STORE,TAX_INVOICE,TIKTOK_PIXEL_ID',
  MULTICHAT:
    'TIENDACHAT_WHATSAPP_WEB,TIENDACHAT_QUICK_REPLIES,TIENDACHAT_TRIGGERS,FUNNEL',
});
